/**
 * The post-move feedback page (073) — the client's three screens:
 *
 *   1. the rating: five stars (the email's star pre-picks one via ?rating=N),
 *      an optional comment, Submit (off until a star is picked);
 *   2. 4–5 stars: thanks, their review with a Copy button, and the review
 *      sites (each one copies the review and opens the site);
 *   3. 1–3 stars: thanks for being honest — nothing else.
 *
 * One rating only: once given, the link always shows its result screen.
 * Every rating and every site opened is a line on the lead's feed (server).
 */

import React, { useState } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

interface Feedback {
  firstName: string;
  origin: string;
  destination: string;
  labels: string[];
  rating: number | null;
  comment: string | null;
  sites: { key: string; label: string; note: string; url: string }[];
}

/** The ?rating=N the email's stars carry, or null. */
function preselected(): number | null {
  const n = Number(new URLSearchParams(window.location.search).get('rating'));
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n : null;
}

/** Clipboard, with the old textarea fallback for browsers without the API. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const t = document.createElement('textarea');
      t.value = text;
      t.setAttribute('readonly', '');
      t.style.position = 'fixed';
      t.style.opacity = '0';
      document.body.appendChild(t);
      t.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(t);
      return ok;
    } catch {
      return false;
    }
  }
}

const starLine = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n);

export function FeedbackView({ feedback, token }: { feedback: Feedback; token: string }) {
  const [state, setState] = useState<Feedback>(feedback);
  const [rating, setRating] = useState<number | null>(feedback.rating ?? preselected());
  const [hover, setHover] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const name = state.firstName;
  const shown = hover ?? rating;

  const submit = async () => {
    if (!rating || sending) return;
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/portal/feedback/${encodeURIComponent(token)}/rate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.data?.feedback) throw new Error(body?.error?.message || 'failed');
      setState(body.data.feedback);
      window.scrollTo(0, 0);
    } catch {
      setError('Sorry, that did not go through. Please try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  // ---- 1. the rating ----
  if (state.rating == null) {
    return (
      <div className="fb">
        <h1 className="fb-h1">{name ? `How did we do, ${name}?` : 'How did we do?'}</h1>
        {state.origin && state.destination && <p className="fb-sub">Your move · {state.origin} → {state.destination}</p>}

        <div className="fb-stars" role="radiogroup" aria-label="Your rating" onMouseLeave={() => setHover(null)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} star${n > 1 ? 's' : ''} — ${state.labels[n - 1]}`}
                    className={`fb-star${shown && n <= shown ? ' on' : ''}`}
                    onClick={() => setRating(n)} onMouseEnter={() => setHover(n)}>
              ★
            </button>
          ))}
        </div>
        <div className="fb-label" aria-live="polite">{shown ? state.labels[shown - 1] : ''}</div>

        <label className="pp-field">
          <span>Tell us about your move (optional)</span>
          <textarea rows={5} value={comment} maxLength={4000} onChange={(e) => setComment(e.target.value)} />
          <div className="fb-hint">Mention your crew or anything that stood out.</div>
        </label>

        {error && <div className="pp-pdf-error">{error}</div>}
        <div style={{ display: 'flex', marginTop: '16px' }}>
          <button type="button" className="pp-btn primary" disabled={!rating || sending} onClick={submit}>
            {sending ? 'Sending…' : 'Submit'}
          </button>
        </div>
      </div>
    );
  }

  // ---- 3. 1–3 stars ----
  if (state.rating <= 3) {
    return (
      <div className="fb">
        <h1 className="fb-h1">{name ? `Thank you for being honest with us, ${name}.` : 'Thank you for being honest with us.'}</h1>
        <p className="fb-p">We’re sorry your move wasn’t everything it should have been. Your feedback has gone directly to our management team, and we read every word.</p>
        <p className="fb-p">We’ll use it to make things better for the next family we move.</p>
      </div>
    );
  }

  // ---- 2. 4–5 stars ----
  const review = (state.comment ?? '').trim();
  const openSite = (site: Feedback['sites'][number]) => {
    // The link opens the site itself (a real <a>, so no pop-up blocker); this copies and logs.
    if (review) void copyText(review);
    void fetch(`${API_BASE_URL}/portal/feedback/${encodeURIComponent(token)}/click`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ site: site.key }), keepalive: true,
    }).catch(() => undefined);
  };
  const copyReview = async () => {
    if (await copyText(review)) { setCopied(true); setTimeout(() => setCopied(false), 2500); }
  };

  return (
    <div className="fb">
      <h1 className="fb-h1">{name ? `Thank you, ${name}!` : 'Thank you!'}</h1>
      <p className="fb-p">We’re so glad your move went well. Would you share your review publicly? It helps other families find a mover they can trust.</p>

      {review && (
        <>
          <div className="fb-review">
            <div className="fb-review-stars" aria-label={`${state.rating} stars`}>{starLine(state.rating)}</div>
            <div className="fb-review-text">{review}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '18px' }}>
            <button type="button" className="pp-btn pp-btn-sm" onClick={copyReview}>Copy review</button>
            {copied && <span className="fb-copied">Copied</span>}
          </div>
        </>
      )}

      {state.sites.length > 0 && (
        <>
          {review && <p className="fb-p" style={{ fontWeight: 700, marginBottom: '4px' }}>Then paste it on any of these:</p>}
          <div className="fb-sites">
            {state.sites.map((s) => (
              <a key={s.key} className="fb-site" href={s.url} target="_blank" rel="noopener noreferrer" onClick={() => openSite(s)}>
                <span className="fb-site-name">{s.label}</span>
                <span className="fb-site-note">{s.note}</span>
              </a>
            ))}
          </div>
          {review && <p className="fb-small">Tapping a site copies your review and opens the review page. Just paste and post.</p>}
        </>
      )}
    </div>
  );
}
